/**
 * Base URL for same-origin `/api/*` routes (tweet fetch server).
 * - Dev: `''` — Vite proxy forwards `/api` → localhost:3001 (`npm run server`).
 * - Prod: set `VITE_API_URL` to the API origin (no trailing slash), e.g. `https://api.example.com`.
 *   If unset, requests go to the static host’s `/api/...` (only works if a reverse proxy routes there).
 */
function normalizeTweetApiBaseUrl(raw: string | undefined): string {
  if (typeof raw !== 'string') return '';
  let u = raw.trim();
  if (!u) return '';
  // Tolerate pasting "VITE_API_URL=https://..." into the Vercel value field.
  u = u.replace(/^VITE_API_URL=/i, '').trim();
  // Fix https:/example (one slash) — common paste typo.
  u = u.replace(/^https:\/(?!\/)/i, 'https://').replace(/^http:\/(?!\/)/i, 'http://');
  // Strip accidental path suffix — origin only (postWriteApi adds /api/...).
  u = u.replace(/\/api\/.*$/i, '').replace(/\/$/, '');
  if (!/^https?:\/\//i.test(u)) return '';
  return u;
}

export function getTweetApiBaseUrl(): string {
  const configured = normalizeTweetApiBaseUrl(import.meta.env.VITE_API_URL);
  // Dev default: '' → Vite proxy /api → localhost:3001. Set VITE_API_URL to hit Railway directly.
  if (import.meta.env.DEV && !configured) return '';
  return configured;
}

/**
 * Headers for POSTs to the write API. Adds `x-api-key` when `VITE_API_WRITE_KEY`
 * is configured so requests pass the server's `requireApiKey` gate. This is a
 * shared key embedded in the client bundle (a rate-abuse speed-bump, not a secret);
 * on-chain truth is still re-derived by the reconciler.
 */
export function getApiWriteHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = import.meta.env.VITE_API_WRITE_KEY;
  if (typeof key === 'string' && key.trim()) {
    headers['x-api-key'] = key.trim();
  }
  return headers;
}

type WriteApiBody = { ok?: boolean; error?: string };

/** Map failed write-API responses to user-visible copy for launch / vote flows. */
export function describeWriteApiFailure(
  res: Response,
  body: WriteApiBody | null,
  isJson: boolean,
  rawText = '',
): string {
  if (res.status === 401) {
    return 'Launch verification is blocked (API key mismatch). Set matching VITE_API_WRITE_KEY and API_WRITE_KEY, then redeploy.';
  }
  if (res.status === 429) {
    return 'Too many verification requests. Wait a minute and try again.';
  }
  if (!isJson) {
    const apiBase = getTweetApiBaseUrl();
    if (!import.meta.env.DEV && !apiBase) {
      return 'Launch verification is unavailable — VITE_API_URL is not configured for this build.';
    }
    if (/vercel\.app/i.test(apiBase)) {
      return 'Launch verification points at the Vercel site, not the API. Set VITE_API_URL to your Railway URL (e.g. worthy-playfulness-production-….up.railway.app), then redeploy.';
    }
    if (import.meta.env.DEV && !rawText.trim()) {
      return 'Couldn\u2019t reach the tweet API. Run `npm run server` in the repo root (port 3001), then try again.';
    }
    if (!rawText.trim() && res.status >= 500) {
      return 'Tweet API is unreachable or timed out. If testing locally, run `npm run server`; in production, check your Railway API deploy.';
    }
    if (res.status === 405 || res.status === 404 || /^\s*<!DOCTYPE/i.test(rawText)) {
      return 'Launch verification hit the wrong server (not the tweet API). Set VITE_API_URL to your Railway API URL in Vercel, then redeploy.';
    }
    return 'Tweet API returned an unexpected response. Check VITE_API_URL points to your Railway API and that the server is running.';
  }
  const err = typeof body?.error === 'string' ? body.error.trim() : '';
  if (err) {
    if (/missing x_bearer_token/i.test(err)) {
      return 'Post verification is unavailable — X API token is missing on the server.';
    }
    if (/rate limited|429/i.test(err)) {
      return 'X API rate limit hit. Wait a minute and try again.';
    }
    if (/not found|unavailable|limited visibility/i.test(err)) {
      return 'This post could not be found on X. Check the URL is public and try again.';
    }
    return err;
  }
  if (import.meta.env.DEV) {
    return 'Couldn\u2019t verify this post. Is `npm run server` running on port 3001?';
  }
  return 'Couldn\u2019t verify this post right now. Try again in a moment.';
}

/** POST JSON to a write API route and parse `{ ok, error }` bodies safely. */
export async function postWriteApi<T extends WriteApiBody>(
  path: string,
  payload: unknown,
): Promise<{ res: Response; body: T | null; isJson: boolean; text: string }> {
  const base = getTweetApiBaseUrl();
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: getApiWriteHeaders(),
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  try {
    return { res, body: JSON.parse(text) as T, isJson: true, text };
  } catch {
    return { res, body: null, isJson: false, text };
  }
}
